import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { sourceMeta } from '../data/reservationSources';
import {
  SCOPE_FILTER_CATEGORY_LABELS,
  SCOPE_FILTER_FIELDS,
  createFilterRule,
  defaultOperatorsForField,
  defaultValueForField,
  operatorLabel,
  scopeFilterField,
  summarizeFilterRule,
  type ScopeAttrRule,
  type ScopeFilterCombinator,
  type ScopeFilterFieldId,
  type ScopeFilterRule,
  type ScopeFilterState,
} from '../lib/scopeGroupFilters';
import {
  SCOPE_KPI_GROUP_ORDER,
  SCOPE_KPI_METRICS,
  SCOPE_KPI_OPERATORS,
  SCOPE_PERIOD_PRESETS,
  SCOPE_SHARE_SOURCES,
  createKpiRule,
  formatResolvedPeriod,
  isScopeKpiRule,
  kpiAggregationLabel,
  kpiGroupLabel,
  kpiMetricDef,
  kpiMetricLabel,
  kpiOperatorLabel,
  kpiUnitSuffix,
  periodPresetLabel,
  resolveScopePeriod,
  retargetKpiRule,
  scopeToday,
  type ResolvedPeriod,
  type ScopeKpiMetric,
  type ScopeKpiRule,
  type ScopeKpiRuleError,
  type ScopePeriodPreset,
} from '../lib/scopeKpi';
import { Icon } from './shared/Icon';

/** Per-rule data state supplied by the modal (KPI + lazily loaded attributes). */
export type ScopeRuleStatus = {
  loading?: boolean;
  error?: string | null;
  invalid?: ScopeKpiRuleError | null;
  resolved?: ResolvedPeriod;
  onRetry?: () => void;
};

type ScopeGroupFiltersProps = {
  state: ScopeFilterState;
  onChange: (next: ScopeFilterState) => void;
  ruleStatus?: Map<string, ScopeRuleStatus>;
  /** Currencies seen in loaded revenue data; offered on revenue rules. */
  currencies?: string[];
};

const KPI_FIELD_PREFIX = 'kpi:';

function fieldSelectValue(rule: ScopeFilterRule): string {
  return isScopeKpiRule(rule) ? `${KPI_FIELD_PREFIX}${rule.metric}` : rule.field;
}

function syncCombinators(rules: ScopeFilterRule[], combinators: ScopeFilterCombinator[]) {
  const next = [...combinators];
  while (next.length < Math.max(0, rules.length - 1)) {
    next.push('and');
  }
  next.length = Math.max(0, rules.length - 1);
  return next;
}

/** Swap a rule's field / metric, keeping its id (and period for KPI → KPI). */
function retargetRule(rule: ScopeFilterRule, selectValue: string): ScopeFilterRule {
  if (selectValue.startsWith(KPI_FIELD_PREFIX)) {
    const metric = selectValue.slice(KPI_FIELD_PREFIX.length) as ScopeKpiMetric;
    if (isScopeKpiRule(rule)) {
      return retargetKpiRule(rule, metric);
    }
    return { ...createKpiRule(metric), id: rule.id };
  }
  const field = selectValue as ScopeFilterFieldId;
  if (!isScopeKpiRule(rule) && rule.field === field) {
    return rule;
  }
  return { ...createFilterRule(field), id: rule.id };
}

function FieldSelect({
  rule,
  onChange,
}: {
  rule: ScopeFilterRule;
  onChange: (value: string) => void;
}) {
  const { t } = useTranslation('chrome');
  return (
    <select
      className="scope-filter-field"
      value={fieldSelectValue(rule)}
      onChange={e => onChange(e.target.value)}
      aria-label={t('scopeFilters.ui.field')}
    >
      <optgroup label={SCOPE_FILTER_CATEGORY_LABELS.location}>
        {SCOPE_FILTER_FIELDS.filter(f => f.category === 'location').map(f => (
          <option key={f.id} value={f.id}>
            {f.label}
          </option>
        ))}
      </optgroup>
      {SCOPE_KPI_GROUP_ORDER.map(group => (
        <optgroup
          key={group}
          label={`${SCOPE_FILTER_CATEGORY_LABELS.performance} · ${kpiGroupLabel(group)}`}
        >
          {SCOPE_KPI_METRICS.filter(m => m.group === group).map(m => (
            <option key={m.id} value={`${KPI_FIELD_PREFIX}${m.id}`}>
              {kpiMetricLabel(m.id)}
            </option>
          ))}
        </optgroup>
      ))}
      <optgroup label={SCOPE_FILTER_CATEGORY_LABELS.settings}>
        {SCOPE_FILTER_FIELDS.filter(f => f.category === 'settings').map(f => (
          <option key={f.id} value={f.id}>
            {f.label}
          </option>
        ))}
      </optgroup>
    </select>
  );
}

function AttrRuleControls({
  rule,
  onPatch,
}: {
  rule: ScopeAttrRule;
  onPatch: (patch: Partial<ScopeAttrRule>) => void;
}) {
  const { t } = useTranslation('chrome');
  const def = scopeFilterField(rule.field) ?? SCOPE_FILTER_FIELDS[0];
  const operators = defaultOperatorsForField(def);
  return (
    <>
      <select
        value={rule.operator}
        onChange={e => {
          const operator = e.target.value as ScopeAttrRule['operator'];
          // Leaving "has no value" needs a usable value again.
          const value =
            rule.operator === 'isEmpty' && !rule.value ? defaultValueForField(def) : rule.value;
          onPatch({ operator, value });
        }}
        aria-label={t('scopeFilters.ui.condition')}
      >
        {operators.map(op => (
          <option key={op} value={op}>
            {operatorLabel(op)}
          </option>
        ))}
      </select>
      {rule.operator === 'isEmpty' ? (
        <span className="scope-filter-boolean-label" />
      ) : def.kind === 'enum' && def.options ? (
        <select
          value={rule.value}
          onChange={e => onPatch({ value: e.target.value })}
          aria-label={t('scopeFilters.ui.value')}
        >
          {def.options.map(opt => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      ) : (
        <input
          type={def.kind === 'number' ? 'number' : 'text'}
          value={rule.value}
          onChange={e => onPatch({ value: e.target.value })}
          placeholder={t('scopeFilters.ui.valuePlaceholder')}
          aria-label={t('scopeFilters.ui.value')}
        />
      )}
    </>
  );
}

function ValueInput({
  value,
  unit,
  onChange,
  label,
}: {
  value: string;
  unit: string;
  onChange: (value: string) => void;
  label: string;
}) {
  return (
    <span className="scope-kpi-value">
      <input
        type="number"
        inputMode="decimal"
        value={value}
        onChange={e => onChange(e.target.value)}
        aria-label={label}
      />
      {unit && <span className="scope-kpi-unit">{unit}</span>}
    </span>
  );
}

function KpiRuleControls({
  rule,
  onPatch,
  currencies,
}: {
  rule: ScopeKpiRule;
  onPatch: (patch: Partial<ScopeKpiRule>) => void;
  currencies: string[];
}) {
  const { t } = useTranslation('chrome');
  const def = kpiMetricDef(rule.metric);
  const unit = kpiUnitSuffix(rule);
  const currencyOptions = [...new Set([rule.currency ?? 'EUR', ...currencies])].sort();
  return (
    <>
      {def.aggregations.length > 1 && (
        <select
          value={rule.aggregation}
          onChange={e => onPatch({ aggregation: e.target.value as ScopeKpiRule['aggregation'] })}
          aria-label={t('scopeFilters.ui.aggregation')}
        >
          {def.aggregations.map(a => (
            <option key={a} value={a}>
              {kpiAggregationLabel(a)}
            </option>
          ))}
        </select>
      )}
      {rule.metric === 'sourceShare' && (
        <select
          value={rule.source ?? 'walkIn'}
          onChange={e => onPatch({ source: e.target.value as ScopeKpiRule['source'] })}
          aria-label={t('scopeFilters.ui.source')}
        >
          {SCOPE_SHARE_SOURCES.map(s => (
            <option key={s} value={s}>
              {sourceMeta(s).label}
            </option>
          ))}
        </select>
      )}
      {def.family === 'revenue' && (
        <select
          value={rule.currency ?? 'EUR'}
          onChange={e => onPatch({ currency: e.target.value })}
          aria-label={t('scopeFilters.ui.currency')}
        >
          {currencyOptions.map(c => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>
      )}
      <select
        value={rule.operator}
        onChange={e => onPatch({ operator: e.target.value as ScopeKpiRule['operator'] })}
        aria-label={t('scopeFilters.ui.condition')}
      >
        {SCOPE_KPI_OPERATORS.map(op => (
          <option key={op} value={op}>
            {kpiOperatorLabel(op)}
          </option>
        ))}
      </select>
      {rule.operator !== 'noData' && (
        <ValueInput
          value={rule.value}
          unit={unit}
          onChange={value => onPatch({ value })}
          label={t('scopeFilters.ui.value')}
        />
      )}
      {rule.operator === 'between' && (
        <>
          <span className="scope-kpi-and">{t('scopeFilters.ui.betweenAnd')}</span>
          <ValueInput
            value={rule.value2 ?? ''}
            unit={unit}
            onChange={value2 => onPatch({ value2 })}
            label={t('scopeFilters.ui.valueUpper')}
          />
        </>
      )}
    </>
  );
}

function KpiPeriodLine({
  rule,
  status,
  onPatch,
}: {
  rule: ScopeKpiRule;
  status?: ScopeRuleStatus;
  onPatch: (patch: Partial<ScopeKpiRule>) => void;
}) {
  const { t } = useTranslation('chrome');
  const today = scopeToday();
  const resolved = status?.resolved ?? resolveScopePeriod(rule.period);
  const changePreset = (preset: ScopePeriodPreset) => {
    if (preset === 'custom') {
      // Seed the custom range with what the previous preset covered.
      onPatch({ period: { preset: 'custom', start: resolved.start, end: resolved.end } });
    } else {
      onPatch({ period: { preset } });
    }
  };
  return (
    <div className="scope-kpi-period">
      <span className="scope-kpi-in">{t('scopeFilters.ui.in')}</span>
      <select
        value={rule.period.preset}
        onChange={e => changePreset(e.target.value as ScopePeriodPreset)}
        aria-label={t('scopeFilters.ui.period')}
      >
        {SCOPE_PERIOD_PRESETS.map(p => (
          <option key={p} value={p}>
            {periodPresetLabel(p)}
          </option>
        ))}
      </select>
      {rule.period.preset === 'custom' ? (
        <span className="scope-kpi-dates">
          <input
            type="date"
            value={rule.period.start}
            max={today}
            onChange={e =>
              rule.period.preset === 'custom' &&
              onPatch({ period: { ...rule.period, start: e.target.value } })
            }
            aria-label={t('scopeFilters.ui.from')}
          />
          <span>–</span>
          <input
            type="date"
            value={rule.period.end}
            min={rule.period.start || undefined}
            max={today}
            onChange={e =>
              rule.period.preset === 'custom' &&
              onPatch({ period: { ...rule.period, end: e.target.value } })
            }
            aria-label={t('scopeFilters.ui.to')}
          />
        </span>
      ) : (
        <span className="scope-kpi-resolved">{formatResolvedPeriod(resolved)}</span>
      )}
      <RuleStatus status={status} />
    </div>
  );
}

function RuleStatus({ status }: { status?: ScopeRuleStatus }) {
  const { t } = useTranslation('chrome');
  if (!status) return null;
  if (status.invalid) {
    const key =
      status.invalid === 'tooManyPeriods'
        ? 'scopeFilters.ui.tooManyPeriods'
        : `scopeFilters.ui.period_${status.invalid}`;
    return <span className="scope-rule-status is-error">{t(key, { max: 3, days: 366 })}</span>;
  }
  if (status.error) {
    return (
      <span className="scope-rule-status is-error">
        {t('scopeFilters.ui.error')}
        {status.onRetry && (
          <button type="button" className="scope-link-btn" onClick={status.onRetry}>
            {t('scopeFilters.ui.retry')}
          </button>
        )}
      </span>
    );
  }
  if (status.loading) {
    return (
      <span className="scope-rule-status is-loading" role="status">
        <span className="scope-spinner" aria-hidden="true" />
        {t('scopeFilters.ui.loading')}
      </span>
    );
  }
  return null;
}

export function ScopeGroupFilters({
  state,
  onChange,
  ruleStatus,
  currencies = [],
}: ScopeGroupFiltersProps) {
  const { t } = useTranslation('chrome');
  // Collapsible so multi-rule filters don't starve the restaurant list.
  const [expanded, setExpanded] = useState(true);

  const replaceRule = (id: string, next: (rule: ScopeFilterRule) => ScopeFilterRule) => {
    const rules = state.rules.map(rule => (rule.id === id ? next(rule) : rule));
    onChange({ rules, combinators: syncCombinators(rules, state.combinators) });
  };

  const removeRule = (id: string) => {
    const index = state.rules.findIndex(r => r.id === id);
    if (index < 0) {
      return;
    }
    const rules = state.rules.filter(r => r.id !== id);
    const combinators = [...state.combinators];
    if (index > 0) {
      combinators.splice(index - 1, 1);
    } else if (combinators.length) {
      combinators.shift();
    }
    onChange({ rules, combinators: syncCombinators(rules, combinators) });
  };

  const addRule = (kind: 'attr' | 'kpi') => {
    const rules = [...state.rules, kind === 'kpi' ? createKpiRule() : createFilterRule()];
    onChange({
      rules,
      combinators: syncCombinators(rules, [...state.combinators, 'and']),
    });
    setExpanded(true);
  };

  const setCombinator = (index: number, combinator: ScopeFilterCombinator) => {
    const combinators = [...state.combinators];
    combinators[index] = combinator;
    onChange({ ...state, combinators });
  };

  const summary = state.rules
    .map(rule => summarizeFilterRule(rule, ruleStatus?.get(rule.id)?.resolved))
    .filter(Boolean);
  const hasRules = state.rules.length > 0;

  return (
    <div className={`scope-filters${expanded ? ' is-expanded' : ' is-collapsed'}`}>
      <div className="scope-filters-head">
        <button
          type="button"
          className="scope-filters-toggle"
          onClick={() => setExpanded(v => !v)}
          aria-expanded={expanded}
        >
          <Icon name="chevron" size={12} />
          <span>
            {t('scopeFilters.ui.filters')}
            {hasRules ? ` (${state.rules.length})` : ''}
          </span>
        </button>
        <div className="scope-filters-add">
          <button type="button" className="scope-filter-add-btn" onClick={() => addRule('kpi')}>
            {t('scopeFilters.ui.addKpi')}
          </button>
          <button type="button" className="scope-filter-add-btn" onClick={() => addRule('attr')}>
            {t('scopeFilters.ui.addFilter')}
          </button>
        </div>
      </div>

      {!expanded && hasRules && (
        <p className="scope-filters-summary" title={summary.join(' · ')}>
          {summary.join(' · ')}
        </p>
      )}

      {expanded && !hasRules && (
        <p className="scope-filters-hint">{t('scopeFilters.ui.hint')}</p>
      )}

      {expanded && (
        <div className="scope-filter-rules">
          {state.rules.map((rule, index) => {
            const status = ruleStatus?.get(rule.id);
            return (
              <React.Fragment key={rule.id}>
                {index > 0 && (
                  <div className="scope-filter-combinator">
                    <button
                      type="button"
                      className={state.combinators[index - 1] === 'and' ? 'on' : ''}
                      onClick={() => setCombinator(index - 1, 'and')}
                    >
                      {t('scopeFilters.ui.and')}
                    </button>
                    <button
                      type="button"
                      className={state.combinators[index - 1] === 'or' ? 'on' : ''}
                      onClick={() => setCombinator(index - 1, 'or')}
                    >
                      {t('scopeFilters.ui.or')}
                    </button>
                  </div>
                )}
                <div
                  className={`scope-filter-rule${isScopeKpiRule(rule) ? ' is-kpi' : ''}`}
                >
                  <div className="scope-filter-rule-main">
                    <FieldSelect
                      rule={rule}
                      onChange={value => replaceRule(rule.id, r => retargetRule(r, value))}
                    />
                    {isScopeKpiRule(rule) ? (
                      <KpiRuleControls
                        rule={rule}
                        currencies={currencies}
                        onPatch={patch =>
                          replaceRule(rule.id, r => ({ ...(r as ScopeKpiRule), ...patch }))
                        }
                      />
                    ) : (
                      <AttrRuleControls
                        rule={rule}
                        onPatch={patch =>
                          replaceRule(rule.id, r => ({ ...(r as ScopeAttrRule), ...patch }))
                        }
                      />
                    )}
                    <button
                      type="button"
                      className="scope-filter-remove-btn"
                      onClick={() => removeRule(rule.id)}
                      aria-label={t('scopeFilters.ui.remove')}
                    >
                      <Icon name="close" size={14} />
                    </button>
                  </div>
                  {isScopeKpiRule(rule) ? (
                    <KpiPeriodLine
                      rule={rule}
                      status={status}
                      onPatch={patch =>
                        replaceRule(rule.id, r => ({ ...(r as ScopeKpiRule), ...patch }))
                      }
                    />
                  ) : (
                    (status?.loading || status?.error) && (
                      <div className="scope-kpi-period">
                        <RuleStatus status={status} />
                      </div>
                    )
                  )}
                </div>
              </React.Fragment>
            );
          })}
        </div>
      )}
    </div>
  );
}
